/**
 * router 的浏览器接线单例：window.history/location/popstate → createRouter →
 * globalStore.set({ nav })。组件读栈走 useGlobal((s) => s.nav)，导航走 nav/actions。
 * main.tsx 在首次渲染前调用 appRouter().init()。
 *
 * **hash 路由**（21ah-5，人工指图「打包要 hash 模式」）：地址形如 `/#/chat/dm/101`。
 * 换模式只换这一层——router 与 routes 里流动的始终是应用内 path（`/chat/dm/101`），
 * 转换口径全在 `hashLocation.ts`（可单测）。这么做产物能丢在任何静态托管/子目录下
 * 直接跑：服务端不需要「未知路径重写到 index.html」那条规则，刷新与深链都不 404。
 */
import { createRouter, type HistoryLike, type Router } from './router';
import { appPathOf, hashUrlFor } from './hashLocation';
import { globalStore } from '~/store/global';

/** 站点基路径（vite base，缺省 '/'）：子目录部署时 hash 前缀要带上它 */
const BASE_URL = import.meta.env.BASE_URL || '/';

/** HistoryLike 是 2 参签名，原生 pushState/replaceState 是 3 参（中间是废弃的 title 位），必须适配。 */
const browserHistory: HistoryLike = {
  get state() {
    return window.history.state as unknown;
  },
  // 入参是应用内 path，写进地址栏时套上 base + '#'
  pushState: (state, url) => window.history.pushState(state, '', hashUrlFor(BASE_URL, url)),
  replaceState: (state, url) =>
    window.history.replaceState(state, '', hashUrlFor(BASE_URL, url)),
  back: () => window.history.back(),
  go: (delta) => window.history.go(delta),
};

let singleton: Router | undefined;

export function appRouter(): Router {
  if (!singleton) {
    singleton = createRouter({
      history: browserHistory,
      getPath: () => appPathOf(BASE_URL, window.location.pathname, window.location.hash),
      onPopState(listener) {
        const handler = (e: PopStateEvent): void => listener(e.state);
        window.addEventListener('popstate', handler);
        return () => window.removeEventListener('popstate', handler);
      },
      onChange(nav) {
        globalStore().set({ nav });
      },
    });
  }
  return singleton;
}
